// ======================================================================
// FILE:
// src/modules/finance/transactions/manual_journal/components/JournalLinesTable.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Manual Journal - Create Journal
//
// PURPOSE:
// Reusable journal-line entry component for the Manual Journal Create
// screen.
//
// RESPONSIBILITIES:
// - Display journal accounting lines
// - Account selection
// - Cost Center selection
// - Line description
// - Debit amount
// - Credit amount
// - Add journal line
// - Delete journal line
// - Calculate line-level totals for display
// - Maintain Debit/Credit exclusivity at UI level
//
// DOES NOT HANDLE:
// - Journal header
// - Journal header API
// - Journal line API submission
// - Save Draft
// - Submit for Approval
// - Navigation
// - Toast / notification handling
// - Global state management
//
// ARCHITECTURE:
// - React functional component
// - Bootstrap
// - React Select / AsyncSelect
// - Controlled journal-line collection
// - No Redux
//
// IMPORTANT:
// - Backend remains the authoritative source for accounting validation.
// - UI validation is provided only for immediate user feedback.
// - A journal line cannot contain both Debit and Credit.
// - A journal line cannot contain neither Debit nor Credit.
// - Currency Amount is derived from the entered Debit/Credit amount by
//   the parent transaction implementation.
// - Project is intentionally not displayed because it is not present in
//   the finalized Create Journal wireframe.
//
// AUTHOR:
// DizyPro ERP
// ======================================================================

import React from "react";

import AsyncSelect from "react-select/async";

// ======================================================================
// COMPONENT
// ======================================================================

export default function JournalLinesTable({

    // --------------------------------------------------------------
    // Controlled journal lines
    //
    // Expected structure:
    //
    // [
    //     {
    //         id: null,
    //         line_no: 10,
    //         coa_id: null,
    //         account_option: null,
    //         cost_center_id: null,
    //         cost_center_option: null,
    //         description: "",
    //         debit_amount: "",
    //         credit_amount: ""
    //     }
    // ]
    // --------------------------------------------------------------

    lines = [],

    // --------------------------------------------------------------
    // Parent callback
    //
    // onLinesChange(updatedLines)
    // --------------------------------------------------------------

    onLinesChange,

    // --------------------------------------------------------------
    // Lookup functions supplied by parent.
    //
    // These are deliberately supplied as props because no verified
    // Finance COA / Cost Center lookup API was assumed here.
    //
    // Expected:
    // loadAccounts(inputValue)
    // loadCostCenters(inputValue)
    // --------------------------------------------------------------

    loadAccounts,

    loadCostCenters,

    // --------------------------------------------------------------
    // Read-only / loading state
    // --------------------------------------------------------------

    disabled = false,

    loading = false,

}) {

    // ==================================================================
    // SAFE UPDATE
    // ==================================================================

    const updateLines = (
        updatedLines
    ) => {

        if (
            typeof onLinesChange !==
            "function"
        ) {
            return;
        }

        onLinesChange(
            updatedLines
        );
    };

    // ==================================================================
    // ADD LINE
    // ==================================================================

    const handleAddLine = () => {

        const nextLineNo =
            lines.length > 0
                ? (
                    Math.max(
                        ...lines.map(
                            (line, index) =>
                                Number(
                                    line.line_no
                                ) ||
                                (
                                    (index + 1) *
                                    10
                                )
                        )
                    ) + 10
                )
                : 10;

        const newLine = {

            id: null,

            line_no:
                nextLineNo,

            coa_id:
                null,

            account_option:
                null,

            cost_center_id:
                null,

            cost_center_option:
                null,

            description:
                "",

            debit_amount:
                "",

            credit_amount:
                "",

        };

        updateLines(
            [
                ...lines,
                newLine,
            ]
        );
    };

    // ==================================================================
    // DELETE LINE
    // ==================================================================

    const handleDeleteLine = (
        index
    ) => {

        // --------------------------------------------------------------
        // Do not prevent deletion at this component level.
        //
        // The backend validation requires at least two lines before
        // submission. The parent can therefore decide whether deletion
        // should be allowed in a particular transaction state.
        // --------------------------------------------------------------

        const updatedLines =
            lines.filter(
                (_, lineIndex) =>
                    lineIndex !== index
            );

        updateLines(
            updatedLines
        );
    };

    // ==================================================================
    // ACCOUNT CHANGE
    // ==================================================================

    const handleAccountChange = (
        index,
        option
    ) => {

        const updatedLines =
            lines.map(
                (line, lineIndex) => {

                    if (
                        lineIndex !== index
                    ) {
                        return line;
                    }

                    return {

                        ...line,

                        coa_id:
                            option?.value ||
                            null,

                        account_option:
                            option ||
                            null,

                    };
                }
            );

        updateLines(
            updatedLines
        );
    };

    // ==================================================================
    // COST CENTER CHANGE
    // ==================================================================

    const handleCostCenterChange = (
        index,
        option
    ) => {

        const updatedLines =
            lines.map(
                (line, lineIndex) => {

                    if (
                        lineIndex !== index
                    ) {
                        return line;
                    }

                    return {

                        ...line,

                        cost_center_id:
                            option?.value ||
                            null,

                        cost_center_option:
                            option ||
                            null,

                    };
                }
            );

        updateLines(
            updatedLines
        );
    };

    // ==================================================================
    // DESCRIPTION CHANGE
    // ==================================================================

    const handleDescriptionChange = (
        index,
        value
    ) => {

        const updatedLines =
            lines.map(
                (line, lineIndex) => {

                    if (
                        lineIndex !== index
                    ) {
                        return line;
                    }

                    return {

                        ...line,

                        description:
                            value,

                    };
                }
            );

        updateLines(
            updatedLines
        );
    };

    // ==================================================================
    // DEBIT CHANGE
    // ==================================================================

    const handleDebitChange = (
        index,
        value
    ) => {

        const updatedLines =
            lines.map(
                (line, lineIndex) => {

                    if (
                        lineIndex !== index
                    ) {
                        return line;
                    }

                    const debitValue =
                        value === ""
                            ? ""
                            : value;

                    return {

                        ...line,

                        debit_amount:
                            debitValue,

                        // ------------------------------------------------
                        // Debit and Credit are mutually exclusive.
                        // Entering a Debit clears Credit.
                        // ------------------------------------------------

                        credit_amount:
                            value !== ""
                                ? "0"
                                : line.credit_amount,

                    };
                }
            );

        updateLines(
            updatedLines
        );
    };

    // ==================================================================
    // CREDIT CHANGE
    // ==================================================================

    const handleCreditChange = (
        index,
        value
    ) => {

        const updatedLines =
            lines.map(
                (line, lineIndex) => {

                    if (
                        lineIndex !== index
                    ) {
                        return line;
                    }

                    const creditValue =
                        value === ""
                            ? ""
                            : value;

                    return {

                        ...line,

                        credit_amount:
                            creditValue,

                        // ------------------------------------------------
                        // Credit and Debit are mutually exclusive.
                        // Entering a Credit clears Debit.
                        // ------------------------------------------------

                        debit_amount:
                            value !== ""
                                ? "0"
                                : line.debit_amount,

                    };
                }
            );

        updateLines(
            updatedLines
        );
    };

    // ==================================================================
    // TOTAL DEBIT
    // ==================================================================

    const totalDebit =
        lines.reduce(
            (
                total,
                line
            ) => {

                const amount =
                    Number(
                        line.debit_amount
                    ) || 0;

                return total + amount;

            },
            0
        );

    // ==================================================================
    // TOTAL CREDIT
    // ==================================================================

    const totalCredit =
        lines.reduce(
            (
                total,
                line
            ) => {

                const amount =
                    Number(
                        line.credit_amount
                    ) || 0;

                return total + amount;

            },
            0
        );

    // ==================================================================
    // DIFFERENCE
    // ==================================================================

    const difference =
        totalDebit -
        totalCredit;

    // ==================================================================
    // NUMBER FORMATTER
    // ==================================================================

    const formatAmount = (
        amount
    ) => {

        return Number(
            amount || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }
        );
    };

    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <div className="card shadow-sm border-0 mb-4">

            {/* ==========================================================
                SECTION HEADER
            ========================================================== */}

            <div className="card-header bg-white border-bottom">

                <div
                    className="
                        d-flex
                        justify-content-between
                        align-items-center
                        flex-wrap
                        gap-2
                    "
                >

                    <div className="d-flex align-items-center">

                        <div
                            className="
                                rounded-circle
                                bg-primary
                                text-white
                                d-flex
                                align-items-center
                                justify-content-center
                                me-2
                            "
                            style={{
                                width: 30,
                                height: 30,
                                fontSize: 14,
                                fontWeight: 600,
                            }}
                        >
                            2
                        </div>

                        <span className="fw-semibold">
                            Journal Lines
                        </span>

                    </div>

                    <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={
                            handleAddLine
                        }
                        disabled={
                            disabled ||
                            loading
                        }
                    >
                        <i className="fas fa-plus me-1" />
                        Add Line
                    </button>

                </div>

            </div>

            {/* ==========================================================
                TABLE
            ========================================================== */}

            <div className="card-body p-0">

                <div
                    className="table-responsive"
                    style={{
                        overflowX: "auto",
                    }}
                >

                    <table
                        className="
                            table
                            table-bordered
                            table-hover
                            align-middle
                            mb-0
                        "
                        style={{
                            minWidth: "1050px",
                        }}
                    >

                        <thead
                            className="table-light"
                        >

                            <tr>

                                <th
                                    style={{
                                        width: 55,
                                        textAlign: "center",
                                    }}
                                >
                                    #
                                </th>

                                <th
                                    style={{
                                        minWidth: 230,
                                    }}
                                >
                                    Account
                                    <span className="text-danger ms-1">
                                        *
                                    </span>
                                </th>

                                <th
                                    style={{
                                        minWidth: 190,
                                    }}
                                >
                                    Cost Center
                                </th>

                                <th
                                    style={{
                                        minWidth: 210,
                                    }}
                                >
                                    Description
                                </th>

                                <th
                                    style={{
                                        minWidth: 150,
                                        textAlign: "right",
                                    }}
                                >
                                    Debit
                                </th>

                                <th
                                    style={{
                                        minWidth: 150,
                                        textAlign: "right",
                                    }}
                                >
                                    Credit
                                </th>

                                <th
                                    style={{
                                        width: 70,
                                        textAlign: "center",
                                    }}
                                >
                                    Actions
                                </th>

                            </tr>

                        </thead>

                        <tbody>

                            {lines.length === 0 && (

                                <tr>

                                    <td
                                        colSpan="7"
                                        className="
                                            text-center
                                            text-muted
                                            py-5
                                        "
                                    >

                                        <div className="mb-2">

                                            <i
                                                className="
                                                    fas
                                                    fa-file-invoice-dollar
                                                    fa-2x
                                                "
                                            />

                                        </div>

                                        No journal lines added.

                                        <div className="small mt-1">
                                            Click
                                            {" "}
                                            <strong>
                                                Add Line
                                            </strong>
                                            {" "}
                                            to begin.
                                        </div>

                                    </td>

                                </tr>

                            )}

                            {lines.map(
                                (
                                    line,
                                    index
                                ) => (

                                    <tr
                                        key={
                                            line.id ||
                                            `new-line-${index}`
                                        }
                                    >

                                        {/* ==================================
                                            LINE NUMBER
                                        ================================== */}

                                        <td
                                            className="
                                                text-center
                                                text-muted
                                                fw-semibold
                                            "
                                        >
                                            {index + 1}
                                        </td>

                                        {/* ==================================
                                            ACCOUNT
                                        ================================== */}

                                        <td>

                                            <AsyncSelect
                                                cacheOptions
                                                defaultOptions
                                                isClearable
                                                isSearchable
                                                value={
                                                    line.account_option ||
                                                    null
                                                }
                                                loadOptions={
                                                    loadAccounts
                                                }
                                                placeholder={
                                                    "Select Account"
                                                }
                                                onChange={
                                                    (
                                                        option
                                                    ) =>
                                                        handleAccountChange(
                                                            index,
                                                            option
                                                        )
                                                }
                                                isDisabled={
                                                    disabled ||
                                                    loading ||
                                                    typeof loadAccounts !==
                                                        "function"
                                                }
                                                noOptionsMessage={() =>
                                                    "No accounts found"
                                                }
                                                loadingMessage={() =>
                                                    "Loading accounts..."
                                                }
                                                menuPortalTarget={
                                                    document.body
                                                }
                                                styles={{
                                                    menuPortal:
                                                        (
                                                            base
                                                        ) => ({
                                                            ...base,
                                                            zIndex: 9999,
                                                        }),
                                                }}
                                            />

                                        </td>

                                        {/* ==================================
                                            COST CENTER
                                        ================================== */}

                                        <td>

                                            <AsyncSelect
                                                cacheOptions
                                                defaultOptions
                                                isClearable
                                                isSearchable
                                                value={
                                                    line.cost_center_option ||
                                                    null
                                                }
                                                loadOptions={
                                                    loadCostCenters
                                                }
                                                placeholder={
                                                    "Select Cost Center"
                                                }
                                                onChange={
                                                    (
                                                        option
                                                    ) =>
                                                        handleCostCenterChange(
                                                            index,
                                                            option
                                                        )
                                                }
                                                isDisabled={
                                                    disabled ||
                                                    loading ||
                                                    typeof loadCostCenters !==
                                                        "function"
                                                }
                                                noOptionsMessage={() =>
                                                    "No cost centers found"
                                                }
                                                loadingMessage={() =>
                                                    "Loading cost centers..."
                                                }
                                                menuPortalTarget={
                                                    document.body
                                                }
                                                styles={{
                                                    menuPortal:
                                                        (
                                                            base
                                                        ) => ({
                                                            ...base,
                                                            zIndex: 9999,
                                                        }),
                                                }}
                                            />

                                        </td>

                                        {/* ==================================
                                            DESCRIPTION
                                        ================================== */}

                                        <td>

                                            <input
                                                type="text"
                                                className="form-control"
                                                value={
                                                    line.description ||
                                                    ""
                                                }
                                                placeholder={
                                                    "Enter description"
                                                }
                                                maxLength="500"
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        handleDescriptionChange(
                                                            index,
                                                            event.target.value
                                                        )
                                                }
                                                disabled={
                                                    disabled ||
                                                    loading
                                                }
                                            />

                                        </td>

                                        {/* ==================================
                                            DEBIT
                                        ================================== */}

                                        <td>

                                            <input
                                                type="number"
                                                className="
                                                    form-control
                                                    text-end
                                                "
                                                min="0"
                                                step="0.01"
                                                value={
                                                    line.debit_amount ??
                                                    ""
                                                }
                                                placeholder="0.00"
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        handleDebitChange(
                                                            index,
                                                            event.target.value
                                                        )
                                                }
                                                disabled={
                                                    disabled ||
                                                    loading
                                                }
                                            />

                                        </td>

                                        {/* ==================================
                                            CREDIT
                                        ================================== */}

                                        <td>

                                            <input
                                                type="number"
                                                className="
                                                    form-control
                                                    text-end
                                                "
                                                min="0"
                                                step="0.01"
                                                value={
                                                    line.credit_amount ??
                                                    ""
                                                }
                                                placeholder="0.00"
                                                onChange={
                                                    (
                                                        event
                                                    ) =>
                                                        handleCreditChange(
                                                            index,
                                                            event.target.value
                                                        )
                                                }
                                                disabled={
                                                    disabled ||
                                                    loading
                                                }
                                            />

                                        </td>

                                        {/* ==================================
                                            DELETE
                                        ================================== */}

                                        <td
                                            className="text-center"
                                        >

                                            <button
                                                type="button"
                                                className="
                                                    btn
                                                    btn-sm
                                                    btn-outline-danger
                                                "
                                                title="Delete line"
                                                onClick={() =>
                                                    handleDeleteLine(
                                                        index
                                                    )
                                                }
                                                disabled={
                                                    disabled ||
                                                    loading
                                                }
                                            >

                                                <i className="fas fa-trash" />

                                            </button>

                                        </td>

                                    </tr>

                                )
                            )}

                        </tbody>

                        {/* ==================================================
                            TABLE TOTALS
                        ================================================== */}

                        {lines.length > 0 && (

                            <tfoot
                                className="table-light"
                            >

                                <tr>

                                    <td
                                        colSpan="4"
                                        className="text-end fw-semibold"
                                    >
                                        Totals
                                    </td>

                                    <td
                                        className="
                                            text-end
                                            fw-semibold
                                        "
                                    >
                                        ₹{" "}
                                        {formatAmount(
                                            totalDebit
                                        )}
                                    </td>

                                    <td
                                        className="
                                            text-end
                                            fw-semibold
                                        "
                                    >
                                        ₹{" "}
                                        {formatAmount(
                                            totalCredit
                                        )}
                                    </td>

                                    <td />

                                </tr>

                            </tfoot>

                        )}

                    </table>

                </div>

            </div>

            {/* ==========================================================
                BALANCE INDICATOR
            ========================================================== */}

            {lines.length > 0 && (

                <div
                    className="
                        card-footer
                        bg-white
                        border-top
                    "
                >

                    <div
                        className="
                            d-flex
                            justify-content-end
                            align-items-center
                            flex-wrap
                            gap-3
                        "
                    >

                        <span className="text-muted">
                            Difference:
                        </span>

                        <span
                            className={
                                difference === 0
                                    ? "text-success fw-semibold"
                                    : "text-danger fw-semibold"
                            }
                        >
                            ₹{" "}
                            {formatAmount(
                                Math.abs(
                                    difference
                                )
                            )}
                        </span>

                    </div>

                </div>

            )}

        </div>
    );
}